import { useEffect, useState } from 'react';
import { formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { Button, Card, Chip, Field, Input, NumberInput, useConfirm, useToast } from '../ui';

/**
 * دستور ساخت (production work orders) — consume materials into a finished product. Completing a work
 * order values the materials at their moving-average and posts Dr Finished Goods / Cr Inventory /
 * Cr Overhead. Finance/Admin (accounting.post to create/complete).
 */

interface WoRow {
  id: number;
  no: number;
  productCode: string;
  qty: number;
  status: string;
  materialCost: number;
  laborCost: number;
}
interface MatDraft {
  materialCode: string;
  qty: number;
}
// The product's stored BOM (GET /api/product/:code) — the recipe the system already owns, so the WO
// form need not be re-keyed by hand (E14-10). `conversion` is the per-UNIT applied labour+overhead.
interface BomLine {
  material_code: number | null;
  qty: number;
}
interface ProductBom {
  lines: BomLine[];
  conversion: number;
}

const STATUS_TONE: Record<string, 'neutral' | 'success' | 'danger'> = {
  open: 'neutral',
  completed: 'success',
  cancelled: 'danger',
};
const STATUS_FA: Record<string, string> = {
  open: 'باز',
  completed: 'تکمیل‌شده',
  cancelled: 'لغوشده',
};

export function WorkOrderPanel({ onCompleted }: { onCompleted?: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<WoRow[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [productCode, setProductCode] = useState('');
  const [qty, setQty] = useState(1);
  const [laborCost, setLaborCost] = useState(0);
  const [mats, setMats] = useState<MatDraft[]>([{ materialCode: '', qty: 1 }]);
  const [bomBusy, setBomBusy] = useState(false);

  // Prefill materials + labour from the product's own BOM (E14-10): consume the recipe the system already
  // stores instead of hand-keying it. Materials scale with the WO qty; labour = per-unit conversion × qty.
  // Lines with no actual material code (group-priced) can't seed a consumable row, so they're reported, not
  // silently dropped. Non-destructive-by-surprise: only fires on the explicit button.
  async function loadBom() {
    if (!productCode.trim() || qty <= 0) {
      toast('ابتدا کد محصول و تعداد را وارد کنید', 'error');
      return;
    }
    setBomBusy(true);
    const r = await api<ProductBom>(`/api/product/${encodeURIComponent(productCode.trim())}`);
    setBomBusy(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در خواندن فرمول محصول'), 'error');
      return;
    }
    const bom = r.body as ProductBom;
    const round4 = (n: number) => Math.round(n * 1e4) / 1e4;
    const seeded = bom.lines
      .filter((l) => l.material_code != null)
      .map((l) => ({ materialCode: String(l.material_code), qty: round4(l.qty * qty) }));
    const groupOnly = bom.lines.length - seeded.length;
    if (seeded.length === 0) {
      toast('این محصول فرمول ماده‌ای با کد مشخص ندارد', 'error');
      return;
    }
    setMats(seeded);
    setLaborCost(Math.round(bom.conversion * qty));
    toast(
      groupOnly > 0
        ? `${seeded.length} ماده از BOM بارگذاری شد (${groupOnly} ردیف گروهی بدون کد ماده نادیده گرفته شد)`
        : `${seeded.length} ماده از BOM بارگذاری شد`,
    );
  }

  function refresh() {
    void api<{ workOrders: WoRow[] }>('/api/accounting/work-orders').then((r) => {
      if (r.ok) setRows((r.body as { workOrders: WoRow[] }).workOrders);
    });
  }
  useEffect(refresh, []);

  async function create() {
    const materials = mats.filter((m) => m.materialCode.trim() && m.qty > 0);
    if (!productCode.trim() || qty <= 0 || materials.length === 0) {
      toast('کد محصول، مقدار و حداقل یک ماده لازم است', 'error');
      return;
    }
    setBusy(true);
    const r = await api('/api/accounting/work-orders', {
      method: 'POST',
      body: JSON.stringify({ productCode, qty, laborCost, materials }),
    });
    setBusy(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در ایجاد دستور ساخت'), 'error');
      return;
    }
    toast('دستور ساخت ایجاد شد ✓');
    setProductCode('');
    setQty(1);
    setLaborCost(0);
    setMats([{ materialCode: '', qty: 1 }]);
    refresh();
  }

  async function complete(id: number) {
    // Completing consumes the materials and posts Dr Finished-Goods / Cr Inventory to the GL — an accounting
    // event that isn't casually undone. Confirm before a mis-click writes journal entries (E7-04).
    const ok = await confirm({
      title: 'تکمیل دستور ساخت',
      message: `دستور ساخت #${id} تکمیل شود؟ مواد مصرف و سند «کالای ساخته‌شده / موجودی» در دفترکل ثبت می‌شود.`,
      confirmLabel: 'تکمیل و ثبت در دفترکل',
      cancelLabel: 'انصراف',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    const r = await api(`/api/accounting/work-orders/${id}/complete`, { method: 'POST' });
    setBusy(false);
    if (!r.ok) {
      toast(errMsg(r, 'خطا در تکمیل'), 'error');
      return;
    }
    toast('دستور ساخت تکمیل و به دفترکل ثبت شد ✓');
    refresh();
    onCompleted?.();
  }

  return (
    <Card title="دستور ساخت (تولید)">
      <Button variant="secondary" onClick={() => setOpen((s) => !s)}>
        {open ? 'بستن فرم' : 'دستور ساخت جدید'}
      </Button>

      {open ? (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))',
              gap: 12,
            }}
          >
            <Field label="کد محصول">
              <Input value={productCode} onChange={(e) => setProductCode(e.target.value)} />
            </Field>
            <Field label="تعداد">
              <NumberInput value={qty} onValue={setQty} allowDecimal />
            </Field>
            <Field label="دستمزد/سربار (ریال)">
              <NumberInput value={laborCost} onValue={setLaborCost} />
            </Field>
          </div>
          <div className="row" style={{ marginTop: 8, gap: 8, alignItems: 'center' }}>
            <Button variant="secondary" onClick={() => void loadBom()} disabled={bomBusy}>
              {bomBusy ? 'در حال بارگذاری…' : 'بارگذاری از فرمول (BOM)'}
            </Button>
            <span className="muted" style={{ fontSize: '.85em' }}>
              مواد و دستمزد را از فرمول محصول برای این تعداد پُر می‌کند؛ سپس قابل ویرایش است.
            </span>
          </div>
          <table className="table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>کد ماده</th>
                <th>مقدار مصرف</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {mats.map((m, i) => (
                <tr key={i}>
                  <td>
                    <Input
                      value={m.materialCode}
                      onChange={(e) =>
                        setMats((ms) =>
                          ms.map((x, idx) =>
                            idx === i ? { ...x, materialCode: e.target.value } : x,
                          ),
                        )
                      }
                    />
                  </td>
                  <td>
                    <NumberInput
                      value={m.qty}
                      onValue={(v) =>
                        setMats((ms) => ms.map((x, idx) => (idx === i ? { ...x, qty: v } : x)))
                      }
                      allowDecimal
                    />
                  </td>
                  <td>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setMats((ms) => (ms.length > 1 ? ms.filter((_, idx) => idx !== i) : ms))
                      }
                      disabled={mats.length <= 1}
                    >
                      حذف
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <Button
                    variant="secondary"
                    onClick={() => setMats((ms) => [...ms, { materialCode: '', qty: 1 }])}
                  >
                    + ماده
                  </Button>
                </td>
              </tr>
            </tfoot>
          </table>
          <Button onClick={() => void create()} disabled={busy}>
            {busy ? 'در حال ثبت…' : 'ایجاد دستور ساخت'}
          </Button>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <table className="table" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>شماره</th>
              <th>محصول</th>
              <th>تعداد</th>
              <th>بهای مواد</th>
              <th>دستمزد</th>
              <th>وضعیت</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{r.no}</td>
                <td>{r.productCode}</td>
                <td>{r.qty}</td>
                <td>{formatRial(r.materialCost)}</td>
                <td>{formatRial(r.laborCost)}</td>
                <td>
                  <Chip tone={STATUS_TONE[r.status] ?? 'neutral'}>
                    {STATUS_FA[r.status] ?? r.status}
                  </Chip>
                </td>
                <td>
                  {r.status === 'open' ? (
                    <Button variant="secondary" onClick={() => void complete(r.id)} disabled={busy}>
                      تکمیل
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Card>
  );
}
